import { useToggle } from '@vueuse/core';
import { acceptHMRUpdate, defineStore } from 'pinia';
import type {
  CartItem,
  CartItemResponse,
  CartResponse,
} from '~/types/ecommerce';

const defaultCart: CartResponse = {
  id: '',
  items: [],
  subTotal: 0,
  version: '',
};

export const useCartStore = defineStore('cart', () => {
  const { $bus } = useNuxtApp();
  const authStore = useAuthStore();
  const { $ecommerce } = useNuxtApp();

  const guestCartId = useCookie('kygunco_guest_cart_id', {
    secure: true,
    sameSite: 'lax',
    maxAge: YEAR_IN_SECONDS,
  });

  const [visible, toggle] = useToggle();

  const cartId = computed(() => authStore.cartId ?? guestCartId.value);

  const saving = ref(false);

  const { data, status, execute, refresh } = useAsyncData(
    'cart',
    () => $ecommerce.fetch<CartResponse>(`carts/${cartId.value}`)
      .catch((error) => {
        if (isFetchError(error) && error.status == 404) {
          guestCartId.value = null;
          return defaultCart;
        }

        handleFetchError(error);

        return defaultCart;
      }),
    {
      default: () => defaultCart,
      immediate: !!cartId.value,
    },
  );

  const save = async (items: CartItem[] = []) => {
    const path = cartId.value ? `carts/${cartId.value}` : 'carts';
    const method = cartId.value ? 'PUT' : 'POST';

    saving.value = true;

    try {
      const response = await $ecommerce.fetch<CartResponse>(path, {
        method: method,
        body: { items },
      });

      if (method == 'POST') {
        // Persist the guest cart id
        guestCartId.value = response.id;
      }

      data.value = response;
    }
    catch (error) {
      data.value = defaultCart;

      if (isFetchError(error) && error.status == 404) {
        guestCartId.value = null;
        return;
      }

      handleFetchError(error);
    }
    finally {
      saving.value = false;
    }
  };

  const add = async (item: CartItem) => {
    if (!data.value) {
      await save([item]);
      return;
    }

    const items = data.value.items.map(
      i =>
        ({
          productId: i.productId,
          quantity: i.quantity,
          variationId: i.variationId,
        }) as CartItem,
    );

    const index = items.findIndex(
      i => i.productId == item.productId && i.variationId == item.variationId,
    );

    const _ = index >= 0 ? (items[index]!.quantity += item.quantity) : items.push(item);

    await save(items);

    visible.value = true;
  };

  const update = async (item: CartItemResponse) => {
    if (!data.value) {
      // TODO: handle this edge case
      return;
    }

    const items = data.value.items;

    const index = items.findIndex(i => i.id == item.id);

    const _ = index >= 0 ? (items[index] = item) : items.push(item);

    await save(
      items.map(
        i =>
          ({
            productId: i.productId,
            quantity: i.quantity,
            variationId: i.variationId,
          }) as CartItem,
      ),
    );
  };

  const clear = async () => {
    const items = data.value?.items ?? [];

    await save([]);

    if (items.length) {
      $bus.emit('cart:remove', items);
    }
  };

  const remove = async (item: CartItemResponse) => {
    if (!data.value) {
      // TODO: handle this edge case
      return;
    }

    await save(
      data.value.items
        .filter(i => i.id != item.id)
        .map(
          i =>
            ({
              productId: i.productId,
              quantity: i.quantity,
              variationId: i.variationId,
            }) as CartItem,
        ),
    );

    $bus.emit('cart:remove', [item]);
  };

  const pending = computed(() => status.value == 'pending' || saving.value);

  const class3 = computed(
    () => !!data.value && data.value.items.some(i => i.isClass3),
  );

  watch(cartId, async (value) => {
    if (!value) {
      data.value = defaultCart;
      return;
    }

    await execute();
  });

  $bus.on('checkout:complete', () => refresh());

  return {
    data,
    pending,
    visible,
    cartId,
    guestCartId,
    class3,
    toggle,
    refresh,
    add,
    update,
    remove,
    clear,
  };
});

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useCartStore, import.meta.hot));
}
